/* eslint-disable @typescript-eslint/no-magic-numbers */

import { ReportType } from '@modeso/types__aumico-lib-annualreport-be';
import { cloneDeep } from 'lodash-es';
import type { ISheetDates } from '../interfaces/sheets-dates.interface';

function addMonthsUTC(base: Date, months: number): Date {
    const y = base.getUTCFullYear();
    const m = base.getUTCMonth();
    const d = base.getUTCDate();

    const total = m + months;
    const ty = y + Math.floor(total / 12);
    const tm = ((total % 12) + 12) % 12;

    // Check if the input date is the last day of its month
    const lastDayOfInputMonth = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
    const isLastDayOfMonth = d === lastDayOfInputMonth;

    let day: number;
    if (isLastDayOfMonth) {
        // If input is last day of month, use last day of target month
        day = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
    } else {
        // Otherwise, use the original day (or last day of target month if original doesn't exist)
        const lastDayOfTargetMonth = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
        day = Math.min(d, lastDayOfTargetMonth);
    }

    const result = new Date(Date.UTC(ty, tm, day, 12, 0, 0, 0));

    return result;
}

export class DatesCalculatorUtil {
    public static calculateFinancialStatementDates(
        balanceSheetCurrentYearDate: string,
        reportType: ReportType | undefined,
    ): ISheetDates {
        let balanceSheetCurrentDate = new Date();
        let balancePreviousYearDate = new Date();
        let incomeCurrentYearFromDate = new Date();
        let incomeCurrentYearToDate = new Date();
        let incomePreviousYearFromDate = new Date();
        let incomePreviousYearToDate = new Date();

        if (reportType === ReportType.ANNUAL || reportType == null) {
            balanceSheetCurrentDate = cloneDeep<Date>(new Date(balanceSheetCurrentYearDate));
            balancePreviousYearDate = new Date(balanceSheetCurrentDate);
            balancePreviousYearDate.setFullYear(balancePreviousYearDate.getFullYear() - 1);

            incomeCurrentYearFromDate = new Date(balancePreviousYearDate);
            incomeCurrentYearFromDate.setDate(incomeCurrentYearFromDate.getDate() + 1);

            incomeCurrentYearToDate = new Date(balanceSheetCurrentDate);

            incomePreviousYearFromDate = new Date(balancePreviousYearDate);
            incomePreviousYearFromDate.setFullYear(incomePreviousYearFromDate.getFullYear() - 1);
            incomePreviousYearFromDate.setDate(incomePreviousYearFromDate.getDate() + 1);

            incomePreviousYearToDate = new Date(balancePreviousYearDate);
        } else if (reportType === ReportType.QUARTERLY) {
            balanceSheetCurrentDate = cloneDeep<Date>(new Date(balanceSheetCurrentYearDate));
            balancePreviousYearDate = addMonthsUTC(balanceSheetCurrentDate, -3);

            incomeCurrentYearFromDate = new Date(balancePreviousYearDate);
            incomeCurrentYearFromDate.setDate(incomeCurrentYearFromDate.getDate() + 1);

            incomeCurrentYearToDate = new Date(balanceSheetCurrentDate);

            incomePreviousYearFromDate = addMonthsUTC(balancePreviousYearDate, -3);
            incomePreviousYearFromDate.setDate(incomePreviousYearFromDate.getDate() + 1);

            incomePreviousYearToDate = new Date(balancePreviousYearDate);
        } else if (reportType === ReportType.MONTHLY) {
            balanceSheetCurrentDate = cloneDeep<Date>(new Date(balanceSheetCurrentYearDate));
            balancePreviousYearDate = addMonthsUTC(balanceSheetCurrentDate, -1);

            incomeCurrentYearFromDate = new Date(balancePreviousYearDate);
            incomeCurrentYearFromDate.setDate(incomeCurrentYearFromDate.getDate() + 1);

            incomeCurrentYearToDate = new Date(balanceSheetCurrentDate);

            incomePreviousYearFromDate = addMonthsUTC(balancePreviousYearDate, -1);
            incomePreviousYearFromDate.setDate(incomePreviousYearFromDate.getDate() + 1);

            incomePreviousYearToDate = new Date(balancePreviousYearDate);
        }

        return {
            balanceCurrentYearDate: new Date(Date.UTC(balanceSheetCurrentDate.getUTCFullYear(), balanceSheetCurrentDate.getUTCMonth(), balanceSheetCurrentDate.getUTCDate(), 0, 0, 0, 0)),
            balancePreviousYearDate: new Date(Date.UTC(balancePreviousYearDate.getUTCFullYear(), balancePreviousYearDate.getUTCMonth(), balancePreviousYearDate.getUTCDate(), 0, 0, 0, 0)),
            incomeCurrentYearFromDate: new Date(Date.UTC(incomeCurrentYearFromDate.getUTCFullYear(), incomeCurrentYearFromDate.getUTCMonth(), incomeCurrentYearFromDate.getUTCDate(), 0, 0, 0, 0)),

            incomeCurrentYearToDate: new Date(Date.UTC(incomeCurrentYearToDate.getUTCFullYear(), incomeCurrentYearToDate.getUTCMonth(), incomeCurrentYearToDate.getUTCDate(), 0, 0, 0, 0)),
            incomePreviousYearFromDate: new Date(Date.UTC(incomePreviousYearFromDate.getUTCFullYear(), incomePreviousYearFromDate.getUTCMonth(), incomePreviousYearFromDate.getUTCDate(), 0, 0, 0, 0)),

            incomePreviousYearToDate: new Date(Date.UTC(incomePreviousYearToDate.getUTCFullYear(), incomePreviousYearToDate.getUTCMonth(), incomePreviousYearToDate.getUTCDate(), 0, 0, 0, 0)),
        };
    }

    public static calculateFinancialStatementDatesFromAutofillDates(
        balanceSheetCurrentYearDate: string,
        reportType: ReportType | undefined,
        balancePreviousYearDateString: string,
        incomePreviousYearFromDateString: string,
        incomePreviousYearToDateString: string,
        previousReportType: ReportType | undefined,
    ): ISheetDates {
        let balanceSheetCurrentDate = new Date();
        let balancePreviousYearDate = new Date(balancePreviousYearDateString);
        let incomePreviousYearFromDate = new Date(incomePreviousYearFromDateString);
        let incomePreviousYearToDate = new Date(incomePreviousYearToDateString);
        let incomeCurrentYearFromDate = new Date(balancePreviousYearDate);
        let incomeCurrentYearToDate = new Date(balancePreviousYearDate);

        if (reportType === ReportType.ANNUAL || reportType == null) {
            balanceSheetCurrentDate = cloneDeep<Date>(new Date(balanceSheetCurrentYearDate));

            incomeCurrentYearFromDate = new Date(balancePreviousYearDate);
            incomeCurrentYearFromDate.setDate(incomeCurrentYearFromDate.getDate() + 1);

            incomeCurrentYearToDate = new Date(balanceSheetCurrentDate);
        } else if (reportType === ReportType.QUARTERLY || reportType === ReportType.MONTHLY) {
            balanceSheetCurrentDate = cloneDeep<Date>(new Date(balanceSheetCurrentYearDate));

            if (previousReportType !== ReportType.ANNUAL) {
                // Only add 1 day for previous interim reports because the previous interim report already has the correct year date
                incomeCurrentYearFromDate = new Date(incomePreviousYearToDate);
                incomeCurrentYearFromDate.setDate(incomeCurrentYearFromDate.getDate() + 1);
            } else {
                // Add 1 day for previous annual report but manually set the year to be the same as the balance sheet current date year
                incomeCurrentYearFromDate = new Date(incomePreviousYearToDate);
                incomeCurrentYearFromDate.setDate(incomeCurrentYearFromDate.getDate() + 1);
                incomeCurrentYearFromDate.setFullYear(balanceSheetCurrentDate.getFullYear());
            }

            incomeCurrentYearToDate = new Date(balanceSheetCurrentDate);
        }

        return {
            balanceCurrentYearDate: new Date(Date.UTC(balanceSheetCurrentDate.getUTCFullYear(), balanceSheetCurrentDate.getUTCMonth(), balanceSheetCurrentDate.getUTCDate(), 0, 0, 0, 0)),
            balancePreviousYearDate: new Date(Date.UTC(balancePreviousYearDate.getUTCFullYear(), balancePreviousYearDate.getUTCMonth(), balancePreviousYearDate.getUTCDate(), 0, 0, 0, 0)),
            incomeCurrentYearFromDate: new Date(Date.UTC(incomeCurrentYearFromDate.getUTCFullYear(), incomeCurrentYearFromDate.getUTCMonth(), incomeCurrentYearFromDate.getUTCDate(), 0, 0, 0, 0)),
            incomeCurrentYearToDate: new Date(Date.UTC(incomeCurrentYearToDate.getUTCFullYear(), incomeCurrentYearToDate.getUTCMonth(), incomeCurrentYearToDate.getUTCDate(), 0, 0, 0, 0)),
            incomePreviousYearFromDate: new Date(Date.UTC(incomePreviousYearFromDate.getUTCFullYear(), incomePreviousYearFromDate.getUTCMonth(), incomePreviousYearFromDate.getUTCDate(), 0, 0, 0, 0)),
            incomePreviousYearToDate: new Date(Date.UTC(incomePreviousYearToDate.getUTCFullYear(), incomePreviousYearToDate.getUTCMonth(), incomePreviousYearToDate.getUTCDate(), 0, 0, 0, 0)),
        };
    }
}
